import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PasswordChangeModal from "./components/PasswordChangeModal";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import NewBusinesses from "./pages/NewBusinesses";
import Scans from "./pages/Scans";
import Businesses from "./pages/Businesses";
import Settings from "./pages/Settings";
import Discover from "./pages/Discover";
import MyLeads from "./pages/MyLeads";
import Team from "./pages/Team";
import MetaAds from "./pages/MetaAds";
import PicToLead from "./pages/PicToLead";
import { BottomTabBar } from "./components/BottomTabBar";
import { usePlanAccess } from "./hooks/usePlanAccess";
import { PlanGate } from "./components/PlanGate";
import { UpgradeDialogHost } from "./components/UpgradeDialog";
import AdminDashboard from "./pages/AdminDashboard";
import Billing from "./pages/Billing";
import Plans from "./pages/Plans";
import LandingPage from "./pages/LandingPage";
import { Toaster } from "@/components/ui/sonner";
import { Sparkles, Menu, X, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Sidebar({ mobileOpen, setMobileOpen }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const subscription = usePlanAccess();
  const [betaFeatures, setBetaFeatures] = useState([]);
  
  useEffect(() => {
    const loadBeta = async () => {
      try {
        const res = await axios.get(`${API}/beta/access`);
        setBetaFeatures((res.data.features || []).filter((f) => f.has_access));
      } catch (error) {
        // beta access is optional; ignore
      }
    };
    if (user) {
      loadBeta();
    }
  }, [user]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);
  
  // Plan-based menu: Free/Basic see Pic-to-Lead + locked Discover/Team; Starter+ unlock lead tools
  const f = subscription?.features || {};
  const isOwner = subscription?.is_owner !== false;
  const trial = subscription?.discover_trial;
  let navItems = [
    { path: "/dashboard", label: "Dashboard", icon: "📊" },
    { path: "/pic-to-lead", label: "Pic-to-Lead", icon: "📸" },
  ];
  if (subscription?.features) {
    navItems.push({ path: "/discover", label: "Discover", icon: "🔍", locked: !f.discover,
      badge: f.discover && trial?.active ? `${trial.remaining} free` : null });
  }
  if (f.my_leads) {
    navItems.push(
      { path: "/my-leads", label: "My Leads", icon: "📇" },
      { path: "/new-businesses", label: "New Businesses", icon: "🆕" },
      { path: "/scans", label: "Scan History", icon: "📋" },
      { path: "/businesses", label: "Businesses", icon: "🏢" },
    );
  }
  if (subscription?.features && (f.team || isOwner)) {
    navItems.push({ path: "/team", label: "Team", icon: "👥", locked: !f.team });
  }
  if (isOwner) navItems.push({ path: "/billing", label: "Billing", icon: "💳" });
  navItems.push({ path: "/settings", label: "Settings", icon: "⚙️" });

  // Beta features the user has access to (e.g. Meta Ads)
  if (betaFeatures.some((f) => f.key === "meta_ads")) {
    navItems.splice(navItems.length - 1, 0, { path: "/meta-ads", label: "Meta Ads", icon: "📣" });
  }

  // Add Admin Dashboard for admin users
  if (user && user.role === "admin") {
    navItems.push({ path: "/admin", label: "Admin", icon: "🔐" });
  }
  
  return (
    <>
      <BottomTabBar features={subscription?.features} />
      {/* Mobile Overlay */}
      <div 
        className={`sidebar-overlay ${mobileOpen ? 'visible' : ''}`}
        onClick={() => setMobileOpen(false)}
      />
      
      <div className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-icon">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h1>Revolutionary Leads</h1>
              <p className="sidebar-subtitle">Lead Scanner</p>
            </div>
          </div>
          {user && (
            <div className="user-badge">
              <div className="user-avatar">{user.username?.charAt(0).toUpperCase()}</div>
              <div className="user-info">
                <span className="user-name">{user.username}</span>
                <span className="user-role">{user.role === 'admin' ? 'Administrator' : 'User'}</span>
              </div>
            </div>
          )}
        </div>
        
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''} ${item.locked ? 'nav-locked' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              {item.locked && <span className="nav-lock" data-testid={`nav-lock-${item.path.slice(1)}`}><Lock size={12} /> Upgrade</span>}
              {item.badge && <span className="nav-badge" data-testid={`nav-badge-${item.path.slice(1)}`}>{item.badge}</span>}
            </Link>
          ))}
          
          <button
            onClick={logout}
            className="nav-item logout-btn"
            data-testid="logout-btn"
          >
            <span className="nav-icon">🚪</span>
            <span className="nav-label">Logout</span>
          </button>
        </nav>
        
        <div className="sidebar-footer">
          <p>Powered by</p>
          <p className="brand">Revo Digital</p>
        </div>
      </div>
    </>
  );
}

function AppContent() {
  const { isAuthenticated, forcePasswordChange, clearForcePasswordChange } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);
  const handlePasswordChanged = () => {
    clearForcePasswordChange();
  };

  return (
    <>
      {/* Mandatory Password Change Modal */}
      <PasswordChangeModal 
        isOpen={isAuthenticated && forcePasswordChange} 
        onPasswordChanged={handlePasswordChanged}
      />
      
      <Routes>
        {/* Public routes */}
        <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />} />
        <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
        
        {/* Protected routes */}
        <Route path="/*" element={
          <ProtectedRoute>
            <div className="app-container">
              {/* Mobile Header */}
              <div className="mobile-header">
                <div className="mobile-header-logo">
                  <div className="logo-icon">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <h1>Revolutionary Leads</h1>
                </div>
                <button 
                  className="mobile-menu-btn"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle menu"
                  data-testid="mobile-menu-btn"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
              
              <Sidebar mobileOpen={mobileMenuOpen} setMobileOpen={setMobileMenuOpen} />
              <UpgradeDialogHost />
              <div className="main-content">
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/new-businesses" element={<PlanGate feature="new_businesses" lockAs="lead_tools"><NewBusinesses /></PlanGate>} />
                  <Route path="/discover" element={<PlanGate feature="discover"><Discover /></PlanGate>} />
                  <Route path="/my-leads" element={<PlanGate feature="my_leads" lockAs="lead_tools"><MyLeads /></PlanGate>} />
                  <Route path="/pic-to-lead" element={<PicToLead />} />
                  <Route path="/scans" element={<PlanGate feature="scans" lockAs="lead_tools"><Scans /></PlanGate>} />
                  <Route path="/businesses" element={<PlanGate feature="businesses" lockAs="lead_tools"><Businesses /></PlanGate>} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/plans" element={<Plans />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/team" element={<Team />} />
                  <Route path="/meta-ads" element={<MetaAds />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                </Routes>
              </div>
            </div>
          </ProtectedRoute>
        } />
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
export { API };
